import { type StrictlyFrozenSet, setMinus, setUnion, toReadonlySet } from "utils/sets";
import { balanceAges } from "../../balanceMap";
import { compileBulkRequestFilter, validateBalance } from "./compileBulkRequestFilter";
import { dollarsFromBasisPoints } from "@vyne-shared/utils/currency";
import { useMemo, useReducer } from "react";
import type { BalanceAge } from "api/responses";

export interface BulkRequestFilterState {
    agings: StrictlyFrozenSet<BalanceAge>;
    includeOutstandingClaims: boolean;
    includeActivePaymentPlans: boolean;
    minBalanceEnabled: boolean;
    minBalance: number | null;
    patient: string;
}

export interface BulkRequestFilterAgingAction {
    type: "AgingAction";
    index: number;
    value: boolean;
}

export interface BulkRequestFilterSingleAgingAction {
    type: "SingleAgingAction";
    index: number;
}

export interface BulkRequestFilterAgingResetAction {
    type: "AgingResetAction";
}

export interface BulkRequestFilterMinBalanceEnabledAction {
    type: "MinBalanceEnabledAction";
    value: boolean;
}

export interface BulkRequestFilterMinBalanceAction {
    type: "MinBalanceAction";
    value: number | null;
}

export interface BulkRequestFilterMinBalanceBlurAction {
    type: "MinBalanceBlurAction";
}

export interface BulkRequestFilterPatientAction {
    type: "PatientAction";
    value: string;
}

export interface BulkRequestFilterResetAction {
    type: "ResetAction";
}

export interface BulkRequestIncludeOutstandingClaimsAction {
    type: "IncludeOutstandingClaimsAction";
    value: boolean;
}

export interface BulkRequestIncludeActivePaymentPlansAction {
    type: "IncludeActivePaymentPlansAction";
    value: boolean;
}

export type BulkRequestFilterAction =
    | BulkRequestFilterAgingAction
    | BulkRequestFilterSingleAgingAction
    | BulkRequestFilterAgingResetAction
    | BulkRequestFilterMinBalanceEnabledAction
    | BulkRequestFilterMinBalanceAction
    | BulkRequestFilterMinBalanceBlurAction
    | BulkRequestFilterPatientAction
    | BulkRequestFilterResetAction
    | BulkRequestIncludeOutstandingClaimsAction
    | BulkRequestIncludeActivePaymentPlansAction;

const allBalanceAges = toReadonlySet(balanceAges);

/** Initial bulk request filtering state. */
const initialState = Object.freeze({
    agings: allBalanceAges,
    minBalanceEnabled: false,
    minBalance: null,
    patient: "",
    includeOutstandingClaims: false,
    includeActivePaymentPlans: false,
});

/**
 * Build initial state for bulk request filtering based on the aging index.
 * @param agingIndex The index of the aging bucket to filter by.
 * @returns Initial state for bulk request filtering.
 */
function buildInitialState(agingIndex: number | null) {
    const initialAging = agingIndex !== null ? balanceAges[agingIndex] : undefined;

    return agingIndex !== null && typeof initialAging !== "undefined"
        ? { ...initialState, agings: toReadonlySet([initialAging]) }
        : initialState;
}

/**
 * Hook to establish bulk request filtering state.
 * @returns
 *  - Current filter state, for use with the filter UI.
 *  - Dispatcher used to change the filter state.
 *  - Compiled filter state, for use with actually filtering bulk request items.
 */
export function useBulkRequestFilter(agingIndex: number | null) {
    const [state, dispatch] = useReducer(reduce, buildInitialState(agingIndex));
    const filter = useMemo(() => compileBulkRequestFilter(state), [state]);

    return { state, dispatch, filter } as const;
}

function reduce(prior: BulkRequestFilterState, action: BulkRequestFilterAction): BulkRequestFilterState {
    switch (action.type) {
        case "AgingAction":
            return reduceAging(action, prior);
        case "SingleAgingAction":
            return { ...initialState, agings: reduceSingleAging(action) };
        case "AgingResetAction":
            return { ...prior, agings: allBalanceAges };
        case "MinBalanceEnabledAction":
            return { ...prior, minBalanceEnabled: action.value };
        case "MinBalanceAction":
            return { ...prior, minBalance: action.value };
        case "MinBalanceBlurAction":
            return reduceMinBalanceBlur(prior);
        case "PatientAction":
            return { ...prior, patient: action.value };
        case "IncludeOutstandingClaimsAction":
            return { ...prior, includeOutstandingClaims: action.value };
        case "IncludeActivePaymentPlansAction":
            return { ...prior, includeActivePaymentPlans: action.value };
        case "ResetAction":
            return initialState;
    }
}

function reduceAging(action: BulkRequestFilterAgingAction, prior: BulkRequestFilterState) {
    let { agings } = prior;
    agings = reduceAgingCore(action, agings);

    if (agings.size < 1) {
        agings = allBalanceAges;
    }

    return { ...prior, agings };
}

function reduceAgingCore({ index, value }: BulkRequestFilterAgingAction, agings: StrictlyFrozenSet<BalanceAge>) {
    const affectedAging = balanceAges[index];
    if (typeof affectedAging === "undefined") {
        console.warn(`No balance age defined for index ${index}`);
        // take no action
        return agings;
    }

    const actionAgings = toReadonlySet([affectedAging]); // always a one-element set

    return value ? setUnion(agings, actionAgings) : setMinus(agings, actionAgings);
}

function reduceSingleAging({ index }: BulkRequestFilterSingleAgingAction) {
    const affectedAging = balanceAges[index];
    if (typeof affectedAging === "undefined") {
        console.warn(`No balance age defined for index ${index}`);
        // take no action
        return allBalanceAges;
    }

    const actionAgings = toReadonlySet([affectedAging]); // always a one-element set

    return actionAgings;
}

function reduceMinBalanceBlur(prior: BulkRequestFilterState) {
    // Wait until user has expressed they are done by blurring the control before we validate the result and
    // write it back. This ensures the user sees the validated value and gets some feedback.
    const validBalance = validateBalance(prior.minBalance ?? 0);
    return { ...prior, minBalance: dollarsFromBasisPoints(validBalance) };
}
